import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
    Shield, Users, UserCheck, LayoutDashboard, Bell, LogOut, Menu, X
} from 'lucide-react';

export default function AdminLayout({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [admin, setAdmin] = useState(null);

    useEffect(() => {
        const adminData = localStorage.getItem('admin_user');
        if (!adminData) {
            navigate('/admin/login');
            return;
        }
        setAdmin(JSON.parse(adminData));
    }, [navigate]);

    const handleLogout = () => {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin/login');
    };

    const navItems = [
        { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { path: '/admin/providers', label: 'MediProviders', icon: UserCheck },
        { path: '/admin/users', label: 'Users', icon: Users },
        { path: '/admin/notifications', label: 'Notifications', icon: Bell },
    ];

    const isActive = (path) => location.pathname === path;

    if (!admin) {
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="animate-spin w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-900">
            {/* Mobile Header */}
            <div className="lg:hidden flex items-center justify-between p-4 bg-slate-800 border-b border-slate-700">
                <div className="flex items-center gap-3">
                    <Shield className="w-8 h-8 text-indigo-500" />
                    <span className="font-bold text-white">Admin Portal</span>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
                    {sidebarOpen ? <X className="text-white" /> : <Menu className="text-white" />}
                </Button>
            </div>

            <div className="flex">
                {/* Sidebar */}
                <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-800 border-r border-slate-700 transform transition-transform lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                    <div className="p-6 hidden lg:block">
                        <div className="flex items-center gap-3">
                            <Shield className="w-10 h-10 text-indigo-500" />
                            <div>
                                <h1 className="font-bold text-white text-lg">Admin Portal</h1>
                                <p className="text-xs text-slate-400">MediGuide</p>
                            </div>
                        </div>
                    </div>

                    <nav className="px-4 py-6 space-y-2">
                        {navItems.map((item) => {
                            const Icon = item.icon;
                            return (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                                        isActive(item.path)
                                            ? 'bg-indigo-600 text-white'
                                            : 'text-slate-400 hover:bg-slate-700 hover:text-white'
                                    }`}
                                >
                                    <Icon className="w-5 h-5" />
                                    {item.label}
                                </Link>
                            );
                        })}
                    </nav>

                    <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
                        <div className="flex items-center gap-3 mb-4 px-2">
                            <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold">
                                {admin?.name?.charAt(0) || 'A'}
                            </div>
                            <div>
                                <p className="text-sm font-medium text-white">{admin?.name}</p>
                                <p className="text-xs text-slate-400">{admin?.role}</p>
                            </div>
                        </div>
                        <Button 
                            variant="ghost" 
                            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-700"
                            onClick={handleLogout}
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </aside>

                {/* Main Content */}
                <main className="flex-1 p-6 lg:p-8 overflow-auto min-h-screen">
                    {children}
                </main>
            </div>

            {/* Mobile overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}
        </div>
    );
}
